<script lang="ts">
  import Icon from '$components/icon/icon.svelte';
  import { X } from 'lucide-svelte';
  import { getMessageGlobalStore } from '$stores/message-global.js';

  interface Props {
    open?: boolean;
    role?: 'neutral' | 'info' | 'positive' | 'warning' | 'negative';
    justify?: 'left' | 'center' | 'right';
    showClose?: boolean;
    onclose?: () => void;
    storeId?: string;
  }

  let {
    open = $bindable(false),
    role = 'neutral',
    justify = 'center',
    showClose = false,
    onclose,
    storeId = 'default',
  }: Props = $props();

  function handleClose() {
    onclose?.();
    open = false;
    $host()?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  let ariaRole = $derived(role === 'warning' || role === 'negative' ? 'alert' : 'status');

  let storeOpenValue = $state(false);
  let syncing = false;

  $effect(() => {
    const { messageGlobalOpen } = getMessageGlobalStore(storeId);
    messageGlobalOpen.set(open); // seed store from prop so prop wins on first mount
    const unsub = messageGlobalOpen.subscribe((v) => { storeOpenValue = v; });
    return () => unsub();
  });

  $effect(() => {
    if (storeOpenValue !== open) {
      syncing = true;
      open = storeOpenValue;
      queueMicrotask(() => { syncing = false; });
    }
  });

  $effect(() => {
    if (!syncing) {
      const { messageGlobalOpen } = getMessageGlobalStore(storeId);
      messageGlobalOpen.set(open);
    }
  });

</script>

<style lang="scss">
  :host {
    display: block;
    width: 100%;
  }

  .banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    box-sizing: border-box;
    gap: var(--a2u-ui-core-spacing-6);
    padding: var(--a2u-ui-core-spacing-8) var(--a2u-ui-core-spacing-24);
    background: var(--bg-color, var(--component-bg-color));
    border-bottom: var(--a2u-ui-border-width-base-md) solid var(--border-color, var(--component-border-color));
    color: var(--font-color, var(--component-font-color));
  }

  .label {
    flex: 1;
    display: flex;
    align-items: center;
    font: var(--a2u-ui-font-body-copy-standard);
    font-family: var(--font-family, var(--a2u-ui-body-copy-standard-font-family));
    padding-top: var(--a2u-ui-core-dimension-1);
    padding-bottom: var(--a2u-ui-core-dimension-2);

    &.justify-left   { justify-content: flex-start; }
    &.justify-center { justify-content: center; }
    &.justify-right  { justify-content: flex-end; }
  }

.close-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  .a2u-ui-neutral {
    --component-bg-color: var(--a2u-ui-color-bg-emphasis-subtle);
    --component-border-color: var(--a2u-ui-color-border-emphasis-subtle);
    --component-font-color: var(--a2u-ui-color-text-on-emphasis-subtle);

    .close-btn { --icon-color: var(--a2u-ui-color-text-on-emphasis-subtle); }
  }

  .a2u-ui-info {
    --component-bg-color: var(--a2u-ui-color-bg-info-subtle);
    --component-border-color: var(--a2u-ui-color-border-info-subtle);
    --component-font-color: var(--a2u-ui-color-text-on-info-subtle);

    .close-btn { --icon-color: var(--a2u-ui-color-text-on-info-subtle); }
  }

  .a2u-ui-positive {
    --component-bg-color: var(--a2u-ui-color-bg-positive-subtle);
    --component-border-color: var(--a2u-ui-color-border-positive-subtle);
    --component-font-color: var(--a2u-ui-color-text-on-positive-subtle);

    .close-btn { --icon-color: var(--a2u-ui-color-text-on-positive-subtle); }
  }

  .a2u-ui-warning {
    --component-bg-color: var(--a2u-ui-color-bg-warning-subtle);
    --component-border-color: var(--a2u-ui-color-border-warning-subtle);
    --component-font-color: var(--a2u-ui-color-text-on-warning-subtle);

    .close-btn { --icon-color: var(--a2u-ui-color-text-on-warning-subtle); }
  }

  .a2u-ui-negative {
    --component-bg-color: var(--a2u-ui-color-bg-negative-subtle);
    --component-border-color: var(--a2u-ui-color-border-negative-subtle);
    --component-font-color: var(--a2u-ui-color-text-on-negative-subtle);

    .close-btn { --icon-color: var(--a2u-ui-color-text-on-negative-subtle); }
  }
</style>

{#if open}
  <div class="banner a2u-ui-{role}" role={ariaRole}>
    <div class="label justify-{justify}">
      <slot></slot>
    </div>
    {#if showClose}
      <button class="close-btn" aria-label="Close" onclick={handleClose}>
        <Icon icon={X} size={16} />
      </button>
    {/if}
  </div>
{/if}
